import { createStore } from "vuex";
import axios from "axios";
import { oneBtnDialog, jwtDecoder, convertDeptText } from "@/utils/utils";
import { MedicalDept, YnEnum } from "@/enums/common.enums";
import { RouterEnum } from "@/router/router.enums";
import router from "@/router";

import {
  bridges,
  createWebToAppBridgeConfig,
  WebToAppBridgeMethodType,
} from "@/utils/bridges";
import { isAndroid, isCertilifeHostApp, isIOS } from "@/utils/deviceCheck";
import signup from "./signup";
import { isStudioEmbed, getStudioAccessToken, requestStudioAccessToken, requestStudioLogout, endStudioSession } from "@/utils/studioEmbed";
import { clearTokens, endSupportTab, getAccessToken, getProfile, getRefreshToken, isSupportEnded, isSupportTab, removeProfile, setAccessToken, setProfile, setRefreshToken } from "@/utils/tokenStore";
import { createSupportRefresher, supportEndedUrl } from "@/utils/supportSession";

// 지원 탭 갱신: 쿠키 /auth/refresh 가 아니라 POST /auth/support/refresh(계약 SUPPORT 9), 동시 401 은 한 번만
const refreshSupport = createSupportRefresher({
  post: (path, body) => axios.post(`${process.env.VUE_APP_SERVERURL}${path}`, body),
  store: { getRefreshToken, setAccessToken, setRefreshToken },
});

// 지원되는 locale 목록 (i18n 파일이 존재하는 언어만)
const SUPPORTED_LOCALES = ["ko", "zh", "ja", "en"];

// locale 값 검증 및 정규화 (지원되지 않는 값은 'ko'로 fallback)
const normalizeLocale = (locale) => {
  if (!locale || typeof locale !== "string") return "ko";
  const normalized = locale.toLowerCase().trim();
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : "ko";
};

export default createStore({
  modules: {
    signup,
  },
  state: {
    showSidenav: false,
    accessToken: "",
    refreshToken: "",
    isPasswordVerified: false,
    userName: "",
    hospitalName: "",
    companyImageUrl: "",
    locale: "ko", // 다국어 처리용 locale (fallback: 'ko')
    isLoading: false,
    medicalDept: [],

    deptBoolean: {
      hasDent: false,
      hasOph: false,
      hasPs: false,
      hasDerm: false,
      hasOs: false,
      hasIm: false,
      hasCommon: false,
    },

    hasCheckedVersion: false,
    bannerStatus: {
      isFirstContents: YnEnum.N,
      isFirstLogin: YnEnum.N,
      needsUpdate: false,
    },
    accessTokenObject: {
      accessToken: "",
      refreshToken: "",
    },
    messageCount: 0,

    hospitalInfo: {},
    companyInfoReceived: false,

    accountPermission: {
      accountType: null, // API 응답 전까지 null
      allowedCategories: [],
      permissions: {
        hospitalSettings: false,
        reservationManagement: false,
        issuancePatientManagement: false,
        dentalCert: false,
        ophCert: false,
        plasticCert: false,
        dermaCert: false,
        orthoCert: false,
        internalCert: false,
        vetCert: false,
        generalCert: false,
        paymentHistory: false,
        orderManagement: false,
        electronicContract: false,
        hospitalInfo: false,
        treatmentConsent: false,
        laborContract: false,
        accountManagement: false,
        alimtalkManagement: false,
        counselingChatbotManagement: false,
      },
    },
    passwordChangeRequired: false, // 비밀번호 변경 필요 여부
  },
  mutations: {
    setIsLoading(state, payload) {
      state.isLoading = payload;
    },
    setIsPasswordVerified(state, payload) {
      state.isPasswordVerified = payload;
    },
    setAccessTokenObject(state, payload) {
      state.accessTokenObject = payload;
    },
    setMedicalDept(state, payload) {
      const hasDent = payload.includes(MedicalDept.DENT);
      const hasOph = payload.includes(MedicalDept.OPH);
      const hasPs = payload.includes(MedicalDept.PS);
      const hasDerm = payload.includes(MedicalDept.DERM);
      const hasOs = payload.includes(MedicalDept.OS);
      const hasKm = payload.includes(MedicalDept.KM);
      const hasNs = payload.includes(MedicalDept.NS);
      const hasCs = payload.includes(MedicalDept.CS);
      const hasIm = payload.includes(MedicalDept.IM);
      const hasCommon = payload.includes(MedicalDept.COMMON);
      const hasVet = payload.includes(MedicalDept.VET_COMMON);
      state.medicalDept = payload;
      state.deptBoolean = {
        hasDent,
        hasOph,
        hasPs,
        hasDerm,
        hasOs,
        hasKm,
        hasNs,
        hasCs,
        hasIm,
        hasCommon,
        hasVet,
      };
    },
    setBannerStatus(state, payload) {
      const newStatus = { ...state.bannerStatus, ...payload };
      state.bannerStatus = newStatus;
    },
    setHasCheckedVersion(state, payload) {
      state.hasCheckedVersion = payload;
    },
    setHospitalInfo(state, payload) {
      state.hospitalInfo = payload;
    },
    increaseMessageCount(state) {
      state.messageCount++;
    },
    resetMessageCount(state) {
      state.messageCount = 0;
    },
    setCompanyInfoReceived(state, payload) {
      state.companyInfoReceived = payload;
    },
    setAccountPermission(state, payload) {
      if (payload && payload.permissions) {
        state.accountPermission = payload;
      } else {
        state.accountPermission = {
          accountType: "ADMIN",
          allowedCategories: [],
          permissions: {
            hospitalSettings: true,
            reservationManagement: true,
            issuancePatientManagement: true,
            dentalCert: true,
            ophCert: true,
            plasticCert: true,
            dermaCert: true,
            orthoCert: true,
            internalCert: true,
            vetCert: true,
            generalCert: true,
            paymentHistory: true,
            orderManagement: true,
            electronicContract: true,
            hospitalInfo: true,
            treatmentConsent: true,
            laborContract: true,
            accountManagement: true,
            alimtalkManagement: true,
            counselingChatbotManagement: true,
          },
        };
      }
    },
    // 사용자 정보 mutation (반응형 업데이트를 위해)
    setUserName(state, payload) {
      state.userName = payload;
    },
    setHospitalName(state, payload) {
      state.hospitalName = payload;
    },
    setCompanyImageUrl(state, payload) {
      state.companyImageUrl = payload;
    },
    setLocale(state, payload) {
      // locale 값 검증 및 정규화 (지원되지 않는 값은 'ko'로 fallback)
      state.locale = normalizeLocale(payload);
    },
  },
  actions: {
    setUserInfo({ commit }, payload) {
      // mutation을 통해 state 업데이트 (반응형 watch 동작을 위해)
      if (payload.userName) commit("setUserName", payload.userName);
      if (payload.hospitalName) commit("setHospitalName", payload.hospitalName);
      if (payload.companyImageUrl)
        commit("setCompanyImageUrl", payload.companyImageUrl);
      // locale 값 검증 및 정규화 (지원되지 않는 값은 'ko'로 fallback)
      const locale = normalizeLocale(payload.locale);
      commit("setLocale", locale);
      // localStorage 저장 (undefined/null 방지)
      if (payload.userName != null)
        setProfile("userNameHos", payload.userName);
      if (payload.hospitalName != null)
        setProfile("hospitalNameHos", payload.hospitalName);
      if (payload.companyImageUrl != null)
        setProfile("companyImageUrlHos", payload.companyImageUrl);
      if (payload.supportMsgChannel != null)
        setProfile("supportMsgChannel", payload.supportMsgChannel);
      setProfile("localeHos", locale);
    },
    async getCompanyInfo({ commit, dispatch }) {
      let requestToken = getStudioAccessToken();
      const companyId = jwtDecoder(requestToken);
      if (companyId) {
        try {
          const fetchCompanyInfo = () => axios.get(
            `${process.env.VUE_APP_SERVERURL}/companies/${companyId}`,
            {
              headers: {
                Authorization: `Bearer ${requestToken}`,
              },
            }
          );
          let response;
          try {
            response = await fetchCompanyInfo();
          } catch (error) {
            if (!isStudioEmbed() || error.response?.status !== 401) throw error;
            requestToken = await requestStudioAccessToken();
            response = await fetchCompanyInfo();
          }

          if (isStudioEmbed() && getStudioAccessToken() !== requestToken) return;

          if (response.status === 200 || response.status === 201) {
            const bannerStatus = {};
            bannerStatus.isFirstContents = response.data.isFirstContents;
            bannerStatus.isFirstLogin = response.data.isFirstLogin;
            commit("setHospitalInfo", response.data);
            commit("setMedicalDept", response.data.medicalDept);
            commit("setBannerStatus", bannerStatus);
            commit("setCompanyInfoReceived", true);
            commit("setAccountPermission", response.data.accountPermission);

            // locale 값 검증 및 정규화 (레거시 데이터에 locale 필드 없으면 'ko' fallback)
            const locale = normalizeLocale(response.data.locale);
            commit("setLocale", locale);
            setProfile("localeHos", locale);

            // 사용자 정보가 localStorage에 없으면 설정 (토큰 기반 로그인 대응)
            if (
              !getProfile("hospitalNameHos") ||
              !getProfile("userNameHos")
            ) {
              dispatch("setUserInfo", {
                // 로그인 화면(Signin getCompanyInfoById)과 같은 칸 — 응답의 계정 이름은 accountInfo.name, 로고는 imgUrl
                // (지원 탭·토큰 진입이 없는 칸을 읽어 이름 자리에 병원 이름, 로고 빈칸이던 것, 11-01 H4)
                userName:
                  (isStudioEmbed() && getProfile("userNameHos")) ||
                  response.data.accountInfo?.name || response.data.accounts?.[0]?.name || response.data.name || "",
                hospitalName: response.data.name || "",
                companyImageUrl: response.data.imgUrl || response.data.companyImageUrl || "",
                supportMsgChannel: response.data.supportMsgChannel || "",
                locale: locale,
              });
            }
          }
        } catch (error) {
          if (isStudioEmbed()) {
            if ([401, 403].includes(error.response?.status)) endStudioSession(error);
            throw error;
          }
          console.log(error);
        }
      }
    },
    async requestAccessToken({ commit, dispatch }) {
      if (isStudioEmbed()) {
        const token = await requestStudioAccessToken();
        this.state.accessToken = token;
        commit("setAccessTokenObject", { accessToken: token, refreshToken: "" });
        return token;
      }
      if (isSupportTab()) {
        // 이미 끝난 지원 탭이면 갱신하지 않음(일반 /auth/refresh 로도 넘어가지 않음)
        if (isSupportEnded()) return;
        const result = await refreshSupport();
        if (result.ended) {
          // 갱신을 반복하지 않고 「끝난 지원 탭」으로 묶은 뒤 끝 화면(사유)
          endSupportTab(result.ended);
          window.location.replace(supportEndedUrl(result.ended));
          return;
        }
        this.state.accessToken = result.accessToken;
        commit("setAccessTokenObject", { accessToken: result.accessToken, refreshToken: "" });
        return result.accessToken;
      }
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_SERVERURL}/auth/refresh`,
          null,
          {
            headers: {
              Authorization: `Bearer ${getRefreshToken()}`,
            },
          }
        );
        if (response.status === 200 || response.status === 201) {
          this.state.accessToken = response.data.accessToken;
          this.state.refreshToken = response.data.refreshToken;
          setAccessToken(response.data.accessToken);
          setRefreshToken(response.data.refreshToken);

          if (response.data) commit("setAccessTokenObject", response.data);

          const obj = {};
          obj.success = true;
          obj.accessToken = response.data.accessToken;
          obj.refreshToken = response.data.refreshToken;
          console.log("requestAccessToken success");
          // bridges().webToApp(response.data);
        } else {
          console.log("requestAccessToken failed, try");
          console.log("token not issued");
          // bridges().webToApp({success: false,});

          return;
        }
      } catch (error) {
        console.log(error);
        commit("setIsLoading", false);

        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          if (router.currentRoute.value.path === "/m/certificate-list") {
            const headers = {};
            headers["fbd-tkn"] = getAccessToken();
            const response = await axios.post(
              `${process.env.VUE_APP_SERVERURL}/auth/generate/mobileT`,
              {},
              { headers }
            );
            if (response.data) {
              console.log(response.data);
              if (response.data.accessToken) {
                setAccessToken(response.data.accessToken);
              }
              if (response.data.refreshToken) {
                setRefreshToken(response.data.refreshToken);
              }
            }
            return;
          } else {
            console.log("requestAccessToken failed, catch");
            //MARK: 웹페이지에서 토큰이 만료된 경우 iOS AOS앱은 재로그인을 하도록 요청
            const isMobileApp =
              (isIOS() || isAndroid()) && isCertilifeHostApp();
            if (isMobileApp) {
              window.requestWebToApp_commonMethod(
                createWebToAppBridgeConfig(
                  WebToAppBridgeMethodType.WEB_TO_APP_WEBPAGE_TOKEN_EXPIRED,
                  {}
                )
              );
            } else {
              if (this.state.messageCount === 0)
                await oneBtnDialog(
                  "토큰 만료",
                  "토큰이 만료되었습니다. 다시 로그인해주세요.",
                  false
                );
              commit("increaseMessageCount");
              router.push(RouterEnum.Signin.path);
              document.querySelector("body").classList.remove("modal-open");
            }
            dispatch("clearLocalStorage");
          }
        }
      }
    },
    clearLocalStorage() {
      if (isStudioEmbed()) {
        requestStudioLogout();
        return;
      }
      // 지원 탭이면 지원 끝(래치) — 같은 브라우저의 일반 로그인 localStorage 는 건드리지 않음
      if (isSupportTab()) return clearTokens();
      clearTokens();
      removeProfile("userNameHos");
      // https://certilife.atlassian.net/browse/CERTI-617 - 1회라도 로그인 했는지 이력을 판단하기 위한 근거가 필요해서 주석처리 함.
      // localStorage.removeItem('hospitalNameHos');
      removeProfile("companyImageUrlHos");
      removeProfile("localeHos"); // locale도 초기화 (재로그인 시 새 계정의 locale 적용)
    },
  },
  getters: {
    getMessageCount: (state) => state.messageCount,
    getUserName: (state) => state.userName,
    getHospitalName: (state) => state.hospitalName,
    getCompanyImageUrl: (state) => state.companyImageUrl,
    getLocale: (state) => state.locale,
    getMedicalDept: (state) => state.medicalDept,
    getDeptBoolean: (state) => state.deptBoolean,
    getBannerStatus: (state) => state.bannerStatus,
    getHospitalInfo: (state) => state.hospitalInfo,
    getCompanyInfoReceived: (state) => state.companyInfoReceived,
    getAccountPermission: (state) => state.accountPermission,
    getPermissions: (state) => state.accountPermission?.permissions || {},
  },
});
